'use client';

/**
 * Portal-owned copies of shared-ui's useGoogle / useFacebook hooks
 * (src/state/hooks, v0.5.383): the login screen's only reason to load the
 * package's state module. Logic is verbatim; the Facebook global is read
 * through a guard so the server render pass never touches `window`.
 */

interface GoogleProfileResponse {
  picture?: string;
}

interface FacebookPictureResponse {
  picture?: {
    data?: {
      url?: string;
    };
  };
}

declare global {
  interface Window {
    FB: any;
  }
}

export const useGoogle = () => {
  const getGoogleProfile = async (userToken: string): Promise<string> => {
    const response = await fetch('https://www.googleapis.com/oauth2/v1/userinfo?alt=json', {
      headers: new Headers({
        Authorization: `Bearer ${userToken}`,
      }),
    });

    const data: GoogleProfileResponse = await response.json();
    return data.picture ? data.picture : Promise.reject('No picture URL found in the user info');
  };

  return { getGoogleProfile };
};

export const useFacebook = (appId: string) => {
  const fb = typeof window === 'undefined' ? undefined : window.FB;

  fb?.init({
    appId: appId,
    cookie: true,
    xfbml: true,
    version: 'v11.0',
  });

  const getFacebookProfile = (token: string): Promise<string> => {
    const apiCallPromise = new Promise<string>((resolve, reject) => {
      fb?.api(
        '/me',
        { fields: 'picture', access_token: token },
        (response: FacebookPictureResponse) => {
          if (response?.picture?.data?.url) {
            resolve(response?.picture?.data?.url);
          } else {
            reject(new Error('Profile picture URL not found'));
          }
        }
      );
    });

    const timeoutPromise = new Promise<string>((_, reject) => {
      setTimeout(() => {
        reject(new Error('Request timed out after 5 seconds'));
      }, 5000);
    });

    return Promise.race([apiCallPromise, timeoutPromise]);
  };

  return { getFacebookProfile };
};
